import React, { useCallback, useState } from 'react';
import { Alert, Button, Form, Tab, Tabs } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { useAuth } from '../Auth/AuthProvider';
import { ResourceState, useAdminResource } from '../TenantAdmin/shared';
import ToolUsage from './ToolUsage';
import PeopleUsage from './PeopleUsage';
import Trend from './AnalyticsTrend';
import { Metrics, Breakdowns } from './AnalyticsWidgets';
import './AnalyticsDashboard.css';

const todayUtc = () => new Date().toISOString().slice(0, 10);
export function presetDates(preset) {
  const end = new Date(`${todayUtc()}T00:00:00Z`);
  if (preset === 'yesterday') end.setUTCDate(end.getUTCDate() - 1);
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - ({ '7d': 6, '30d': 29 }[preset] || 0));
  return { from: start.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) };
}
const analyticsError = error => error?.response?.status === 403 ? 'You do not have permission to view these analytics.'
  : error?.response?.status === 404 ? 'This tenant was not found.' : 'Unable to load analytics. Check the date range or try again.';

export default function AnalyticsDashboard({ embedded = false, title = 'Usage analytics' }) {
  const { session, type } = useAuth(); const { tenantId } = useParams();
  const platform = type === 'platform' && !tenantId;
  const endpoint = type === 'platform' ? (tenantId ? `/api/platform/tenants/${encodeURIComponent(tenantId)}/analytics` : '/api/platform/analytics') : '/api/tenant/analytics';
  const [selection, setSelection] = useState('today'); const [dates, setDates] = useState(() => presetDates('today'));
  const [from, setFrom] = useState(todayUtc); const [to, setTo] = useState(todayUtc);
  const [validation, setValidation] = useState(''); const [tab, setTab] = useState('overview'); const [revision, setRevision] = useState(0);
  const Heading = embedded ? 'h2' : 'h1';
  const apply = event => {
    event.preventDefault(); const span = (Date.parse(to) - Date.parse(from)) / 86400000;
    if (!from || !to || !Number.isFinite(span) || span < 0 || span >= 366 || to > todayUtc()) { setValidation('Choose a UTC date range of 1–366 days, ending no later than today.'); return; }
    setValidation(''); setDates({ from, to }); setRevision(value => value + 1);
  };
  const props = { api: session.api, endpoint, ...dates, platform };
  return <section className={`analytics ${embedded ? '' : 'ta-page'}`} aria-label={title}>
    <Heading>{title}</Heading>
    <div className="analytics-filters">
      <Form.Group controlId="analytics-range"><Form.Label>Date range</Form.Label><Form.Select value={selection} onChange={e => {
        setSelection(e.target.value); setValidation('');
        if (e.target.value !== 'custom') setDates(presetDates(e.target.value));
      }}><option value="today">Today</option><option value="yesterday">Yesterday</option><option value="7d">Last 7 days</option><option value="30d">Last 30 days</option><option value="custom">Custom Range</option></Form.Select></Form.Group>
      <Button onClick={() => { if (selection !== 'custom') setDates(presetDates(selection)); setRevision(value => value + 1); }}>Refresh analytics</Button>
      <span>Showing {dates.from} – {dates.to} (UTC)</span>
    </div>
    {selection === 'custom' && <Form onSubmit={apply} className="analytics-filters" aria-label="Custom analytics dates">
      <Form.Group controlId="analytics-from"><Form.Label>From (UTC)</Form.Label><Form.Control type="date" required max={todayUtc()} value={from} onChange={e => setFrom(e.target.value)} /></Form.Group>
      <Form.Group controlId="analytics-to"><Form.Label>To (UTC)</Form.Label><Form.Control type="date" required max={todayUtc()} value={to} onChange={e => setTo(e.target.value)} /></Form.Group>
      <Button type="submit">Apply dates</Button>
    </Form>}
    {validation && <Alert variant="danger" role="alert">{validation}</Alert>}
    <Tabs id="analytics-tabs" activeKey={tab} onSelect={setTab} mountOnEnter unmountOnExit transition={false}>
      <Tab eventKey="overview" title="Overview">{tab === 'overview' && <Overview key={`${endpoint}:${dates.from}:${dates.to}:${revision}`} {...props} />}</Tab>
      <Tab eventKey="most" title="Most Used Tools">{tab === 'most' && <ToolUsage key={`${endpoint}:${dates.from}:${dates.to}:${revision}`} {...props} ranked />}</Tab>
      {platform && <Tab eventKey="tenants" title="Top Tenants">{tab === 'tenants' && <PeopleUsage key={`${endpoint}:${dates.from}:${dates.to}:${revision}`} {...props} tenants />}</Tab>}
      <Tab eventKey="tools" title="Tool Usage">{tab === 'tools' && <ToolUsage key={`${endpoint}:${dates.from}:${dates.to}:${revision}`} {...props} />}</Tab>
      {!platform && <Tab eventKey="users" title="Users">{tab === 'users' && <PeopleUsage key={`${endpoint}:${dates.from}:${dates.to}:${revision}`} {...props} />}</Tab>}
    </Tabs>
  </section>;
}

function Overview({ api, endpoint, from, to, platform }) {
  const resource = useAdminResource(useCallback(async () => {
    const { data } = await api.get(endpoint, { params: { range: 'custom', from, to } });
    if (!data?.executions || !Array.isArray(data.trend)) throw new Error('Invalid analytics response');
    return data;
  }, [api, endpoint, from, to]), analyticsError);
  const data = resource.data;
  return <ResourceState resource={resource}>{data && <div className="analytics-overview">
    {data.tenantName && <p>{data.tenantName}</p>}
    <Metrics counts={data.executions} extra={[...(platform ? [['Active Tenants With Usage', data.activeTenantsWithUsage]] : []), ['Active Users With Usage', data.activeUsersWithUsage]]} />
    {data.executions.total > 0 ? <div className="analytics-overview-charts"><Trend days={data.trend} /><Breakdowns counts={data.executions} /></div> : <p>No tool usage for the selected period.</p>}
  </div>}</ResourceState>;
}
